import { teamLockerConfig } from './config';
import { getLogger } from '@logging/logger';

export class TeamLockerLoginError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'TeamLockerLoginError';
    this.status = status;
  }
}

export interface TeamLockerLoginParams {
  email: string;
  password: string;
}

/**
 * Establishes a TeamLocker session via the OAuth password grant. Sent as
 * multipart/form-data with credentials so the session cookie is set on the
 * teamlocker domain.
 */
export interface TeamLockerTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
  created_at: number;
  locker_manager_id: number;
}

export async function loginToTeamLocker({ email, password }: TeamLockerLoginParams): Promise<TeamLockerTokenResponse> {
  const formData = new FormData();
  formData.append('username[identification]', email);
  formData.append('username[password]', password);
  formData.append('grant_type', 'password');

  let response: Response;
  try {
    response = await fetch(`${teamLockerConfig.baseUrl}/oauth/token`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
  } catch (error) {
    getLogger().error('teamlocker login network error', {}, error);
    throw new TeamLockerLoginError('Unable to reach the authentication service. Please try again.');
  }

  if (!response.ok) {
    const bodyText = await response.text().catch(() => '');
    getLogger().warn('teamlocker login non-ok', { status: response.status, body: bodyText });
    throw new TeamLockerLoginError('We created your account, but signing you in failed. Please log in to continue.', response.status);
  }

  return response.json() as Promise<TeamLockerTokenResponse>;
}

export async function logoutFromTeamLocker(): Promise<void> {
  try {
    await fetch(`${teamLockerConfig.baseUrl}/session`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (error) {
    getLogger().error('teamlocker logout network error', {}, error);
  }
}
